import { useEffect, useState } from "react";

export const VIEWPORT_MODE_TRANSITION_MS = 500;

/**
 * Keeps an element mounted long enough for its exit transition to finish.
 *
 * Entering mounts immediately in a hidden state, then flips visible on the
 * next animation frame so CSS transitions can interpolate from the hidden
 * pose without a flash.
 */
export function usePresenceTransition(
  active: boolean,
  durationMs = VIEWPORT_MODE_TRANSITION_MS,
): { shouldRender: boolean; isVisible: boolean } {
  const [shouldRender, setShouldRender] = useState(active);
  const [isVisible, setIsVisible] = useState(active);

  useEffect(() => {
    let frameId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (active) {
      setShouldRender(true);
      frameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      timeoutId = setTimeout(() => {
        setShouldRender(false);
      }, durationMs);
    }

    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [active, durationMs]);

  return {
    shouldRender: active || shouldRender,
    isVisible,
  };
}
